import { RoomAudioRenderer, RoomContext } from "@livekit/components-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverAnchor,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useCameraPermission } from "@/hooks/use-camera-permission";
import { useMicrophonePermission } from "@/hooks/use-microphone-permission";
import { useScreenShare } from "@/hooks/use-screen-share";
import { livekitUrlFor, livekitUrlIn } from "@/lib/connection-target";
import { SENTRY_ENABLED } from "@/lib/sentry";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { uiStore } from "@/lib/stores/ui-store";
import "@livekit/components-styles";
import { BackgroundBlur, VirtualBackground } from "@livekit/track-processors";
import { captureException } from "@sentry/react";
import { LocalVideoTrack, Room } from "livekit-client";
import {
	Camera,
	CameraOff,
	ChevronUp,
	Mic,
	MicOff,
	Monitor,
	MonitorOff,
	Pause,
	PictureInPicture2,
	Play,
	Radio,
	Square,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useStore } from "zustand";
import { BackgroundEffectApplier } from "./background-selector";
import {
	BroadcastReactionOverlay,
	BroadcastReactions,
} from "./broadcast-reactions";
import { BroadcastRoleSelector } from "./broadcast-role-selector";
import { BroadcastSpeakerManager } from "./broadcast-speaker-manager";
import { BroadcastVideoGrid } from "./broadcast-video-grid";
import { ControlBarSettings } from "./control-bar-settings";
import { ParticipantList } from "./participant-list";
import { ScreenSourcePicker } from "./screen-source-picker";

const BROADCAST_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
	echoCancellation: false,
	noiseSuppression: false,
	autoGainControl: false,
};

export function BroadcastControlPanel() {
	const user = useStore(authStore, (state) => state.user);
	const broadcast = useStore(coreStore, (state) => state.broadcast);
	const startBroadcast = useStore(coreStore, (state) => state.startBroadcast);
	const pauseBroadcast = useStore(coreStore, (state) => state.pauseBroadcast);
	const resumeBroadcast = useStore(coreStore, (state) => state.resumeBroadcast);
	const stopBroadcast = useStore(coreStore, (state) => state.stopBroadcast);
	const generateBroadcastToken = useStore(
		coreStore,
		(state) => state.generateBroadcastToken
	);
	const fetchBroadcastState = useStore(
		coreStore,
		(state) => state.fetchBroadcastState
	);

	const broadcastControlSettings = useStore(
		uiStore,
		(state) => state.broadcastControlSettings
	);
	const setBroadcastControlSettings = useStore(
		uiStore,
		(state) => state.setBroadcastControlSettings
	);
	const room = useStore(uiStore, (state) => state.broadcastRoom);
	const setBroadcastRoom = useStore(uiStore, (state) => state.setBroadcastRoom);
	const navigate = useStore(uiStore, (state) => state.navigate);
	const pipOpenFn = useStore(uiStore, (state) => state.pipOpenFn);

	const [title, setTitle] = useState("");
	const [includedRoles, setIncludedRoles] = useState<string[]>([]);
	const [isConnecting, setIsConnecting] = useState(false);
	const [audioSettingsOpen, setAudioSettingsOpen] = useState(false);
	const [videoSettingsOpen, setVideoSettingsOpen] = useState(false);
	const { requestPermission: requestMicPermission } = useMicrophonePermission();
	const { requestPermission: requestCameraPermission } = useCameraPermission();

	const isMicEnabled = broadcastControlSettings.isMicEnabled;
	const isCameraEnabled = broadcastControlSettings.isCameraEnabled;
	const isScreenShareEnabled = broadcastControlSettings.isScreenShareEnabled;

	const {
		toggleScreenShare: toggleScreenShareInternal,
		handleElectronScreenShare,
		screenPickerOpen,
		setScreenPickerOpen,
		screenSources,
	} = useScreenShare({
		localParticipant: room?.localParticipant,
		sentryContext: "broadcast_screenshare",
		onScreenShareChange: (enabled) =>
			setBroadcastControlSettings({ isScreenShareEnabled: enabled }),
	});

	const isMicEnabledRef = useRef(isMicEnabled);
	const isCameraEnabledRef = useRef(isCameraEnabled);
	const isScreenShareEnabledRef = useRef(isScreenShareEnabled);
	const isConnectingRef = useRef(false);
	const isStoppingRef = useRef(false);
	const isPausingRef = useRef(false);

	const [localVideoStream, setLocalVideoStream] = useState<MediaStream | null>(
		null
	);
	const [localAudioStream, setLocalAudioStream] = useState<MediaStream | null>(
		null
	);

	useEffect(() => {
		fetchBroadcastState();
	}, [fetchBroadcastState]);

	// Redirect non-broadcasters to viewer page when someone else starts a broadcast
	useEffect(() => {
		if (broadcast && user?.uid !== broadcast.startedBy) {
			navigate({ type: "voiceChannel" });
		}
	}, [broadcast, user?.uid, navigate]);

	useEffect(() => {
		if (!broadcast) {
			setBroadcastControlSettings({
				isMicEnabled: false,
				isCameraEnabled: false,
				isScreenShareEnabled: false,
			});
		}
	}, [broadcast, setBroadcastControlSettings]);

	useEffect(() => {
		isMicEnabledRef.current = isMicEnabled;
	}, [isMicEnabled]);

	useEffect(() => {
		isCameraEnabledRef.current = isCameraEnabled;
	}, [isCameraEnabled]);

	useEffect(() => {
		isScreenShareEnabledRef.current = isScreenShareEnabled;
	}, [isScreenShareEnabled]);

	useEffect(() => {
		if (!broadcast || broadcast.status === "live") {
			if (localVideoStream) {
				for (const track of localVideoStream.getTracks()) {
					track.stop();
				}
				setLocalVideoStream(null);
			}
			if (localAudioStream) {
				for (const track of localAudioStream.getTracks()) {
					track.stop();
				}
				setLocalAudioStream(null);
			}
			return;
		}

		const startLocalPreview = async () => {
			try {
				if (isCameraEnabled && !localVideoStream) {
					const videoStream = await navigator.mediaDevices.getUserMedia({
						video: true,
					});
					setLocalVideoStream(videoStream);
				}
				if (isMicEnabled && !localAudioStream) {
					const audioStream = await navigator.mediaDevices.getUserMedia({
						audio: BROADCAST_AUDIO_CONSTRAINTS,
					});
					setLocalAudioStream(audioStream);
				}
			} catch (error) {
				console.error("Failed to start local preview:", error);
				if (SENTRY_ENABLED)
					captureException(error, { tags: { context: "broadcast_preview" } });
			}
		};

		startLocalPreview();
	}, [broadcast?.status, isCameraEnabled, isMicEnabled]);

	useEffect(() => {
		if (!(broadcast && user?.uid)) {
			return;
		}

		if (isStoppingRef.current || isPausingRef.current) {
			return;
		}

		const storeRoom = uiStore.getState().broadcastRoom;
		const isAlreadyConnected = storeRoom?.state === "connected";

		if (
			broadcast.status === "live" &&
			!room &&
			!isConnecting &&
			!isAlreadyConnected
		) {
			connectToRoom();
		} else if (broadcast.status === "paused" && room) {
			disconnectFromRoom();
		}
	}, [broadcast?.status, user?.uid, room, isConnecting]);

	useEffect(
		() => () => {
			if (localVideoStream) {
				for (const track of localVideoStream.getTracks()) {
					track.stop();
				}
			}
			if (localAudioStream) {
				for (const track of localAudioStream.getTracks()) {
					track.stop();
				}
			}
		},
		[]
	);

	useEffect(() => {
		const handleBeforeUnload = () => {
			const currentRoom = uiStore.getState().broadcastRoom;
			if (currentRoom) {
				currentRoom.disconnect();
			}
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
		};
	}, []);

	const connectToRoom = useCallback(async () => {
		if (!user?.uid || isConnecting || isConnectingRef.current) {
			return;
		}

		const existingRoom = uiStore.getState().broadcastRoom;
		if (existingRoom?.state === "connected") {
			return;
		}

		isConnectingRef.current = true;
		setIsConnecting(true);

		try {
			const response = await generateBroadcastToken();
			const { token, canPublish } = response;

			if (!canPublish) {
				toast.error("You don't have permission to publish to this broadcast");
				setIsConnecting(false);
				return;
			}

			if (localVideoStream) {
				for (const track of localVideoStream.getTracks()) {
					track.stop();
				}
				setLocalVideoStream(null);
			}
			if (localAudioStream) {
				for (const track of localAudioStream.getTracks()) {
					track.stop();
				}
				setLocalAudioStream(null);
			}

			const newRoom = new Room();
			await newRoom.connect(livekitUrlFor(livekitUrlIn(response)), token);

			await newRoom.localParticipant.setMicrophoneEnabled(
				isMicEnabledRef.current,
				BROADCAST_AUDIO_CONSTRAINTS
			);

			await newRoom.localParticipant.setCameraEnabled(
				isCameraEnabledRef.current
			);

			if (isScreenShareEnabledRef.current) {
				await newRoom.localParticipant.setScreenShareEnabled(true, {
					audio: true,
				});
			}

			setBroadcastRoom(newRoom);
			toast.success("You are now live!");
		} catch (error) {
			console.error("Failed to connect to room:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "broadcast_connect" } });
			toast.error("Failed to go live");
		} finally {
			isConnectingRef.current = false;
			setIsConnecting(false);
		}
	}, [
		user?.uid,
		generateBroadcastToken,
		isConnecting,
		localVideoStream,
		localAudioStream,
		setBroadcastRoom,
	]);

	const disconnectFromRoom = useCallback(() => {
		if (room) {
			const localTracks = Array.from(
				room.localParticipant.trackPublications.values()
			);
			for (const publication of localTracks) {
				if (publication.track) {
					publication.track.stop();
				}
			}
			room.disconnect();
			setBroadcastRoom(null);
		}
	}, [room, setBroadcastRoom]);

	const handleStart = async () => {
		if (!title.trim()) {
			toast.error("Please enter a broadcast title");
			return;
		}

		try {
			await startBroadcast(title, includedRoles);
			toast.success(
				"Broadcast created - preview your settings before going live"
			);
			setTitle("");
			setIncludedRoles([]);
		} catch (error) {
			console.error("Failed to start broadcast:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "broadcast_start" } });
			toast.error("Failed to start broadcast");
		}
	};

	const handlePause = async () => {
		if (user?.uid !== broadcast?.startedBy) {
			toast.error("Only the broadcaster can pause the broadcast");
			return;
		}
		isPausingRef.current = true;
		try {
			if (room && isScreenShareEnabled) {
				await room.localParticipant.setScreenShareEnabled(false);
				setBroadcastControlSettings({ isScreenShareEnabled: false });
			}
			disconnectFromRoom();
			await pauseBroadcast();
			toast.info("Broadcast paused - you are no longer live");
		} catch (error) {
			console.error("Failed to pause broadcast:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "broadcast_pause" } });
			toast.error("Failed to pause broadcast");
		} finally {
			isPausingRef.current = false;
		}
	};

	const handleResume = async () => {
		if (user?.uid !== broadcast?.startedBy) {
			toast.error("Only the broadcaster can resume the broadcast");
			return;
		}
		try {
			await resumeBroadcast();
		} catch (error) {
			console.error("Failed to resume broadcast:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "broadcast_resume" } });
			toast.error("Failed to go live");
		}
	};

	const handleStop = async () => {
		if (user?.uid !== broadcast?.startedBy) {
			toast.error("Only the broadcaster can end the broadcast");
			return;
		}
		isStoppingRef.current = true;
		try {
			disconnectFromRoom();
			if (localVideoStream) {
				for (const track of localVideoStream.getTracks()) {
					track.stop();
				}
				setLocalVideoStream(null);
			}
			if (localAudioStream) {
				for (const track of localAudioStream.getTracks()) {
					track.stop();
				}
				setLocalAudioStream(null);
			}
			await stopBroadcast();
			toast.info("Broadcast ended");
		} catch (error) {
			console.error("Failed to stop broadcast:", error);
			if (SENTRY_ENABLED)
				captureException(error, { tags: { context: "broadcast_stop" } });
			toast.error("Failed to stop broadcast");
		} finally {
			isStoppingRef.current = false;
		}
	};

	const toggleMic = async () => {
		if (user?.uid !== broadcast?.startedBy) {
			toast.error("Only the broadcaster can control the microphone");
			return;
		}
		const newState = !isMicEnabled;

		if (newState) {
			const granted = await requestMicPermission();
			if (!granted) return;
		}

		setBroadcastControlSettings({ isMicEnabled: newState });

		if (room) {
			try {
				await room.localParticipant.setMicrophoneEnabled(
					newState,
					BROADCAST_AUDIO_CONSTRAINTS
				);
			} catch (error) {
				setBroadcastControlSettings({ isMicEnabled: !newState });
				if (SENTRY_ENABLED)
					captureException(error, { tags: { context: "broadcast_mic" } });
				toast.error("Failed to toggle microphone");
			}
		} else if (broadcast?.status === "paused") {
			if (newState && !localAudioStream) {
				try {
					const audioStream = await navigator.mediaDevices.getUserMedia({
						audio: BROADCAST_AUDIO_CONSTRAINTS,
					});
					setLocalAudioStream(audioStream);
				} catch (error) {
					console.error("Failed to enable microphone:", error);
					if (SENTRY_ENABLED)
						captureException(error, { tags: { context: "broadcast_mic" } });
					setBroadcastControlSettings({ isMicEnabled: false });
				}
			} else if (!newState && localAudioStream) {
				for (const track of localAudioStream.getTracks()) {
					track.stop();
				}
				setLocalAudioStream(null);
			}
		}
	};

	const toggleCamera = async () => {
		if (user?.uid !== broadcast?.startedBy) {
			toast.error("Only the broadcaster can control the camera");
			return;
		}
		const newState = !isCameraEnabled;

		if (newState) {
			const granted = await requestCameraPermission();
			if (!granted) return;
		}

		setBroadcastControlSettings({ isCameraEnabled: newState });

		if (room) {
			try {
				await room.localParticipant.setCameraEnabled(newState);
			} catch (error) {
				setBroadcastControlSettings({ isCameraEnabled: !newState });
				if (SENTRY_ENABLED)
					captureException(error, { tags: { context: "broadcast_camera" } });
				toast.error("Failed to toggle camera");
			}
		} else if (broadcast?.status === "paused") {
			if (newState && !localVideoStream) {
				try {
					const videoStream = await navigator.mediaDevices.getUserMedia({
						video: true,
					});
					setLocalVideoStream(videoStream);
				} catch (error) {
					console.error("Failed to enable camera:", error);
					if (SENTRY_ENABLED)
						captureException(error, { tags: { context: "broadcast_camera" } });
					setBroadcastControlSettings({ isCameraEnabled: false });
				}
			} else if (!newState && localVideoStream) {
				for (const track of localVideoStream.getTracks()) {
					track.stop();
				}
				setLocalVideoStream(null);
			}
		}
	};

	const toggleScreenShare = async () => {
		if (user?.uid !== broadcast?.startedBy) {
			toast.error("Only the broadcaster can control screen sharing");
			return;
		}
		if (!room) {
			toast.info("Screen sharing is only available when live");
			return;
		}
		await toggleScreenShareInternal();
	};

	const handlePopOutToPip = async () => {
		if (!pipOpenFn) return;

		const success = await pipOpenFn();
		if (!success) return;

		const currentGridItems = uiStore.getState().gridItems;
		const firstItem = currentGridItems[0];
		if (!firstItem) return;

		const currentIndex = firstItem.activeTabIndex;
		const otherTabIndex = firstItem.tabs.findIndex(
			(tab, i) =>
				i !== currentIndex &&
				tab.type !== "voiceChannel" &&
				tab.type !== "broadcastControls"
		);

		if (otherTabIndex !== -1) {
			uiStore.getState().setActiveTab(firstItem.id, otherTabIndex);
		} else {
			uiStore.getState().closeTab(firstItem.id, currentIndex);
		}
	};

	if (!broadcast) {
		return (
			<div className="flex h-full flex-col">
				<div className="flex h-14 shrink-0 items-center border-b px-4 max-md:px-14">
					<Radio className="mr-2 size-4" />
					<h2 className="font-semibold">Broadcast Controls</h2>
				</div>

				<div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
					<Radio className="size-16 text-muted-foreground" />
					<h3 className="font-semibold text-lg">Start a Broadcast</h3>
					<p className="text-center text-muted-foreground text-sm">
						Enter a title and start broadcasting to all users.
					</p>
					<div className="flex w-full max-w-sm flex-col gap-3">
						<Input
							onChange={(e) => setTitle(e.target.value)}
							onKeyDown={(e) => e.key === "Enter" && handleStart()}
							placeholder="Broadcast title"
							value={title}
						/>
						<BroadcastRoleSelector
							includedRoles={includedRoles}
							onIncludedRolesChange={setIncludedRoles}
						/>
						<Button className="w-full" onClick={handleStart}>
							<Play className="mr-2 size-4" />
							Start Broadcast
						</Button>
					</div>
				</div>
			</div>
		);
	}

	const isLive = broadcast.status === "live";
	const isPaused = broadcast.status === "paused";

	return (
		<>
			<ScreenSourcePicker
				onOpenChange={setScreenPickerOpen}
				onSelect={handleElectronScreenShare}
				open={screenPickerOpen}
				sources={screenSources}
			/>
			<div className="flex h-full flex-col" data-lk-theme="default">
				<div className="flex h-14 shrink-0 items-center justify-between border-b px-4 max-md:px-14">
					<div className="flex items-center gap-2">
						<Radio
							className={`size-4 ${isLive ? "text-green-500" : "text-yellow-500"}`}
						/>
						<h2 className="font-semibold text-foreground">{broadcast.title}</h2>
						<span
							className={`rounded px-2 py-0.5 font-medium text-xs ${
								isLive
									? "bg-green-500/20 text-green-500"
									: "bg-yellow-500/20 text-yellow-500"
							}`}
						>
							{isLive ? "LIVE" : "PREVIEW"}
						</span>
					</div>
					<div className="flex items-center gap-3">
						<BroadcastSpeakerManager />
						{isLive && room && (
							<RoomContext.Provider value={room}>
								<BroadcastReactions />
								<ParticipantList />
							</RoomContext.Provider>
						)}
						<Button
							onClick={handlePopOutToPip}
							size="sm"
							title="Pop out to Picture-in-Picture"
							variant="secondary"
						>
							<PictureInPicture2 className="size-4" />
						</Button>
						<Button onClick={handleStop} size="sm" variant="destructive">
							<Square className="mr-1 size-3" />
							End
						</Button>
					</div>
				</div>

				<div className="relative flex-1 overflow-hidden bg-muted">
					{isLive && room ? (
						<>
							<RoomContext.Provider value={room}>
								<BackgroundEffectApplier />
								<LiveBroadcastPreview />
								<RoomAudioRenderer />
							</RoomContext.Provider>
							<RoomContext.Provider value={room}>
								<BroadcastReactionOverlay />
							</RoomContext.Provider>
						</>
					) : (
						<LocalPreview
							isCameraEnabled={isCameraEnabled}
							videoStream={localVideoStream}
						/>
					)}

					{isPaused && (
						<div className="pointer-events-none absolute inset-x-0 top-4 flex justify-center">
							<div className="rounded-lg bg-yellow-500/90 px-4 py-2 text-black">
								<p className="text-center font-semibold">Preview Mode</p>
								<p className="text-sm">Click "Go Live" to start broadcasting</p>
							</div>
						</div>
					)}

					{isConnecting && (
						<div className="absolute inset-0 flex items-center justify-center bg-background/50">
							<div className="rounded-lg bg-background px-4 py-2">
								<p className="font-semibold">Going live...</p>
							</div>
						</div>
					)}
				</div>

				<div className="flex items-center justify-center gap-2 border-t bg-card/50 p-4 backdrop-blur-sm">
					{isLive ? (
						<Button
							className="h-12 rounded-full px-6 shadow-sm transition-all hover:shadow-md"
							onClick={handlePause}
							variant="secondary"
						>
							<Pause className="mr-2 size-5" />
							Pause
						</Button>
					) : (
						<Button
							className="h-12 rounded-full px-6 shadow-sm transition-all hover:shadow-md"
							disabled={isConnecting}
							onClick={handleResume}
							variant="destructive"
						>
							<Play className="mr-2 size-5" />
							Go Live
						</Button>
					)}

					<div className="mx-2 h-8 w-px bg-border" />

					<Popover onOpenChange={setAudioSettingsOpen} open={audioSettingsOpen}>
						<PopoverAnchor asChild>
							<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
								<Button
									className="h-12 rounded-none border-0 bg-accent px-4 transition-all hover:bg-accent-foreground/10"
									onClick={toggleMic}
									variant="ghost"
								>
									{isMicEnabled ? (
										<Mic className="size-5 text-foreground" />
									) : (
										<MicOff className="size-5 text-foreground" />
									)}
									<span className="sr-only">
										{isMicEnabled ? "Mute microphone" : "Unmute microphone"}
									</span>
								</Button>
								<div className="h-6 w-px bg-border" />
								<PopoverTrigger asChild>
									<Button
										className="h-12 rounded-none border-0 bg-accent px-2 transition-all hover:bg-accent-foreground/10"
										variant="ghost"
									>
										<ChevronUp
											className={`size-4 text-foreground transition-transform duration-200 ${
												audioSettingsOpen ? "rotate-180" : ""
											}`}
										/>
										<span className="sr-only">Audio settings</span>
									</Button>
								</PopoverTrigger>
							</div>
						</PopoverAnchor>
						<ControlBarSettings
							onOpenChange={setAudioSettingsOpen}
							open={audioSettingsOpen}
							type="audio"
						/>
					</Popover>

					<Popover onOpenChange={setVideoSettingsOpen} open={videoSettingsOpen}>
						<PopoverAnchor asChild>
							<div className="group flex items-center overflow-hidden rounded-full border border-border bg-background shadow-sm transition-all hover:shadow-md">
								<Button
									className="h-12 rounded-none border-0 bg-accent px-4 transition-all hover:bg-accent-foreground/10"
									onClick={toggleCamera}
									variant="ghost"
								>
									{isCameraEnabled ? (
										<Camera className="size-5 text-foreground" />
									) : (
										<CameraOff className="size-5 text-foreground" />
									)}
									<span className="sr-only">
										{isCameraEnabled ? "Turn off camera" : "Turn on camera"}
									</span>
								</Button>
								<div className="h-6 w-px bg-border" />
								<PopoverTrigger asChild>
									<Button
										className="h-12 rounded-none border-0 bg-accent px-2 transition-all hover:bg-accent-foreground/10"
										variant="ghost"
									>
										<ChevronUp
											className={`size-4 text-foreground transition-transform duration-200 ${
												videoSettingsOpen ? "rotate-180" : ""
											}`}
										/>
										<span className="sr-only">Video settings</span>
									</Button>
								</PopoverTrigger>
							</div>
						</PopoverAnchor>
						<ControlBarSettings
							onOpenChange={setVideoSettingsOpen}
							open={videoSettingsOpen}
							type="video"
						/>
					</Popover>

					<Button
						className="h-12 rounded-full border border-border bg-accent px-4 shadow-sm transition-all hover:shadow-md hover:brightness-90"
						disabled={!isLive}
						onClick={toggleScreenShare}
						variant="ghost"
					>
						{isScreenShareEnabled ? (
							<MonitorOff className="size-5 text-foreground" />
						) : (
							<Monitor className="size-5 text-foreground" />
						)}
						<span className="sr-only">
							{isLive
								? isScreenShareEnabled
									? "Stop sharing"
									: "Share screen"
								: "Screen share only available when live"}
						</span>
					</Button>
				</div>
			</div>
		</>
	);
}

function LocalPreview({
	videoStream,
	isCameraEnabled,
}: {
	videoStream: MediaStream | null;
	isCameraEnabled: boolean;
}) {
	const videoRef = useRef<HTMLVideoElement>(null);
	const trackRef = useRef<LocalVideoTrack | null>(null);
	const isProcessingRef = useRef(false);
	const backgroundSettings = useStore(
		uiStore,
		(state) => state.backgroundSettings
	);

	useEffect(() => {
		if (!videoStream) {
			if (trackRef.current) {
				trackRef.current.stop();
				trackRef.current = null;
			}
			return;
		}

		const videoTrack = videoStream.getVideoTracks()[0];
		if (!videoTrack) return;

		const localTrack = new LocalVideoTrack(videoTrack, undefined, false);
		trackRef.current = localTrack;

		if (videoRef.current) {
			localTrack.attach(videoRef.current);
		}

		const applyProcessor = async () => {
			if (isProcessingRef.current) return;
			isProcessingRef.current = true;
			try {
				if (backgroundSettings.type === "blur") {
					await localTrack.setProcessor(
						BackgroundBlur(backgroundSettings.blurAmount || 10)
					);
				} else if (
					backgroundSettings.type === "image" &&
					backgroundSettings.imageUrl
				) {
					await localTrack.setProcessor(
						VirtualBackground(backgroundSettings.imageUrl)
					);
				} else {
					await localTrack.stopProcessor();
				}
			} catch {
				// Ignore errors
			} finally {
				isProcessingRef.current = false;
			}
		};

		applyProcessor();

		return () => {
			localTrack.stop();
			localTrack.detach();
			trackRef.current = null;
		};
	}, [
		videoStream,
		backgroundSettings.type,
		backgroundSettings.imageUrl,
		backgroundSettings.blurAmount,
	]);

	if (!(isCameraEnabled && videoStream)) {
		return (
			<div className="flex h-full items-center justify-center">
				<div className="flex flex-col items-center gap-2 text-muted-foreground">
					<CameraOff className="size-16" />
					<p>Camera is off</p>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-full items-center justify-center">
			<video
				autoPlay
				className="h-full max-h-full w-auto max-w-full object-contain"
				muted
				playsInline
				ref={videoRef}
				style={{ transform: "scaleX(-1)" }}
			/>
		</div>
	);
}

function LiveBroadcastPreview() {
	return <BroadcastVideoGrid variant="full" />;
}
